import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle, XCircle, Camera, StopCircle } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";

export function CheckInPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const [scanning, setScanning] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [successOpen, setSuccessOpen] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const scannerRef = useRef(null);
    const readerId = "qr-reader-box";
    const lockRef = useRef(false);

    const start = async () => {
        if (scanning) return;
        setScanning(true);
        const html5Qr = new Html5Qrcode(readerId);
        scannerRef.current = html5Qr;
        try {
            await html5Qr.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 260, height: 260 } },
                async (decoded) => {
                    if (lockRef.current) return;
                    lockRef.current = true;
                    try {
                        const { data } = await api.post(`/api/events/${id}/check-in`, { token: decoded });
                        const details = `${data.ticket_type} - ${data.holder_name}`;
                        setLastResult({ success: true, msg: t("organizer.checked_in"), details });
                        setSuccessData({ msg: t("organizer.checked_in"), details });
                        setSuccessOpen(true);
                        toast.success(t("organizer.checked_in"));
                    } catch (e) {
                        const detail = e?.response?.data?.detail || t("organizer.invalid_ticket");
                        const already = (detail || "").toString().toLowerCase().includes("already");
                        setLastResult({ success: false, msg: already ? t("organizer.already_checked") : t("organizer.invalid_ticket"), details: detail });
                        toast.error(detail);
                    } finally {
                        setTimeout(() => { lockRef.current = false; }, 1500);
                    }
                },
                () => {}
            );
        } catch (e) {
            toast.error("Camera access denied");
            setScanning(false);
        }
    };

    const stop = async () => {
        if (scannerRef.current) {
            try { await scannerRef.current.stop(); } catch {}
            try { await scannerRef.current.clear(); } catch {}
            scannerRef.current = null;
        }
        setScanning(false);
    };

    useEffect(() => {
        return () => { stop(); };
        // eslint-disable-next-line
    }, []);

    return (
        <div data-testid="checkin-page" className="space-y-4">
            <div className="bento-card p-5 md:p-6 space-y-4">
                <div id={readerId} className="w-full max-w-md mx-auto aspect-square rounded-2xl overflow-hidden bg-black" />
                <div className="flex justify-center">
                    {!scanning ? (
                        <button data-testid="scan-start" onClick={start} className="btn-primary inline-flex items-center gap-2">
                            <Camera className="h-4 w-4" />
                            {t("organizer.scan")}
                        </button>
                    ) : (
                        <button data-testid="scan-stop" onClick={stop} className="btn-outline inline-flex items-center gap-2">
                            <StopCircle className="h-4 w-4" />
                            {t("organizer.stop_scan")}
                        </button>
                    )}
                </div>
                {lastResult && (
                    <div
                        data-testid="scan-result"
                        className={`p-4 rounded-xl flex items-start gap-3 ${lastResult.success ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"}`}
                    >
                        {lastResult.success ? <CheckCircle className="h-6 w-6 flex-shrink-0" /> : <XCircle className="h-6 w-6 flex-shrink-0" />}
                        <div>
                            <div className="font-bold">{lastResult.msg}</div>
                            {lastResult.details && <div className="text-sm mt-1 opacity-80">{lastResult.details}</div>}
                        </div>
                    </div>
                )}
            </div>

            {/* Success-only confirmation dialog so the staff explicitly acknowledges a valid check-in */}
            <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
                <DialogContent className="sm:max-w-md" data-testid="checkin-success-dialog">
                    <DialogHeader>
                        <div className="mx-auto mb-2 w-14 h-14 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
                            <CheckCircle className="h-8 w-8 text-[hsl(var(--success))]" />
                        </div>
                        <DialogTitle className="text-center font-heading font-black text-2xl text-[hsl(var(--success))]">
                            {successData?.msg}
                        </DialogTitle>
                        {successData?.details && (
                            <DialogDescription className="text-center font-medium pt-1">
                                {successData.details}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    <button
                        data-testid="checkin-success-continue"
                        onClick={() => setSuccessOpen(false)}
                        className="btn-primary w-full mt-2"
                    >
                        {t("common.continue")}
                    </button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
